import { TravelSimRegistrationRequestModel } from "features/travelSimRegistration/data/model/TravelSimRegistrationRequestModel";
import { ServiceEnum } from "utils/enums/ServiceEnum";

export interface SelectOption {
  label?: string;
  value?: string;
}

export interface SearchFormValues {
  created?: Array<Record<string, unknown>>;
  status?: number | string;
  code?: string;
  transRef?: string;
  supplierRefCode?: string;
  packageCode?: string[];
  simType?: number | string;
  carrierCode?: string[];
  nation?: string;
  phoneNumber?: string;
  buyerPhone?: string;
  partnerCode?: string;
  userRef?: string[];
  serial?: string;
  storeCode?: string;
}

export interface SearchContext {
  staffOptions: SelectOption[];
  userOptions: SelectOption[];
  selectedProviderCode?: string;
  selectedPartnerType: number;
  isStore: boolean;
  storeCode?: string;
  limit: number;
}

export class TravelSimRegistrationQuery {
  static resolveUserRef(
    rawUserRef: string[] | undefined,
    staffOptions: SelectOption[],
    userOptions: SelectOption[],
    selectedPartnerType: number,
    isStore: boolean,
  ): string[] | undefined {
    if (!rawUserRef || rawUserRef.length === 0) return undefined;

    if (selectedPartnerType === 1 && isStore) {
      return rawUserRef.filter((ele) =>
        staffOptions?.some((child) => child?.value === ele),
      );
    }

    if (selectedPartnerType === 2 || (selectedPartnerType === 1 && !isStore)) {
      return rawUserRef.filter((ele) =>
        userOptions?.some((child) => child?.value === ele),
      );
    }

    return undefined;
  }

  static sanitize<T extends Record<string, unknown>>(obj: T): T {
    const result = { ...obj };
    Object.keys(result).forEach((key) => {
      const value = result[key];
      if (
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) &&
          (value.length === 0 || value.every((v) => !v || v === "")))
      ) {
        delete result[key];
      }
    });
    return result as T;
  }

  static buildSearchQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): TravelSimRegistrationRequestModel {
    const finalUserRef = this.resolveUserRef(
      formValues.userRef,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new TravelSimRegistrationRequestModel({
      fromDate: dateFrom,
      toDate: dateTo,
      serial: formValues.serial,
      status:
        formValues.status !== undefined ? Number(formValues.status) : undefined,
      code: formValues.code,
      transRef: formValues.transRef,
      supplierRefCode: formValues.supplierRefCode,
      packageCode: formValues.packageCode,
      simType:
        formValues.simType !== undefined
          ? Number(formValues.simType)
          : undefined,
      carrierCode: formValues.carrierCode,
      nation: formValues.nation,
      phoneNumber: formValues.phoneNumber,
      buyerPhone: formValues.buyerPhone,
      storeCodes: [context.storeCode ?? formValues.storeCode],
      userRef: finalUserRef,
      partnerCodes: [formValues.partnerCode],
      providerCode: context.selectedProviderCode,
      page: 0,
      limit: context.limit ?? 10,
      serviceCode: ServiceEnum.TRAVEL_SIM,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as TravelSimRegistrationRequestModel;
  }

  static buildExportQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): TravelSimRegistrationRequestModel {
    const finalUserRef = this.resolveUserRef(
      formValues.userRef,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new TravelSimRegistrationRequestModel({
      fromDate: dateFrom,
      toDate: dateTo,
      serial: formValues.serial,
      status:
        formValues.status !== undefined ? Number(formValues.status) : undefined,
      code: formValues.code,
      transRef: formValues.transRef,
      supplierRefCode: formValues.supplierRefCode,
      packageCode: formValues.packageCode,
      simType:
        formValues.simType !== undefined
          ? Number(formValues.simType)
          : undefined,
      carrierCode: formValues.carrierCode,
      nation: formValues.nation,
      phoneNumber: formValues.phoneNumber,
      buyerPhone: formValues.buyerPhone,
      storeCodes: [context.storeCode ?? formValues.storeCode],
      userRef: finalUserRef,
      partnerCodes: [formValues.partnerCode],
      providerCode: context.selectedProviderCode,
      serviceCode: ServiceEnum.TRAVEL_SIM,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as TravelSimRegistrationRequestModel;
  }

  static buildTableChangeQuery(
    prevParams: TravelSimRegistrationRequestModel,
    formValues: Partial<SearchFormValues>,
    page: number,
    limit: number,
  ): TravelSimRegistrationRequestModel {
    const status =
      formValues.status !== undefined
        ? typeof formValues.status === "string"
          ? Number(formValues.status)
          : formValues.status
        : prevParams.status;

    const simType =
      formValues.simType !== undefined
        ? typeof formValues.simType === "string"
          ? Number(formValues.simType)
          : formValues.simType
        : prevParams.simType;

    const raw = new TravelSimRegistrationRequestModel({
      ...prevParams,
      ...formValues,
      status: status as number | undefined,
      simType: simType as number | undefined,
      page,
      limit,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as TravelSimRegistrationRequestModel;
  }

  static buildRedoQuery(
    fromDate: string,
    toDate: string,
  ): TravelSimRegistrationRequestModel {
    return new TravelSimRegistrationRequestModel({
      page: 0,
      limit: 10,
      fromDate,
      toDate,
      serviceCode: ServiceEnum.TRAVEL_SIM,
    });
  }
}
